import Bluebird from 'bluebird'
import {
  CreateReducer,
  CreateAction,
} from '../utils/store'
import {
  getProjectInfo,
} from '../utils/api'

import {
  getFilestoreUploadUrl,
} from '@kaidam/fc-shared/src/utils/url'

import {
  FileUploadStatus,
  FileUploadState,
  ReducerHandler,
  ThunkHandler,
} from '../../types/store'

import {
  PayloadSetProgress,
  PayloadWithFiles,
} from '../../types/fileupload'

import {
  PayloadUploadFiles,
} from '../../types/filestore'

import {
  IField_Storage_File,
} from '@kaidam/fc-shared/src/types/storage'

import {
  NetworkThunk,
} from '../utils/network'

const prefix = 'fileupload'

/*

  initial state

*/
const initialState: FileUploadState = {
  // are we currently uploading
  inProgress: false,

  // a map of filename onto an object with:
  //  * startime
  //  * percentDone
  //  * remainingTime (calculated by the startTime, nowTime and percentageDone)
  status: {},
}

/*

  utils

*/
const resetState = (state: FileUploadState) => {
  state.inProgress = false
  state.status = {}
}

const getFileUrlQuery = (file: File, path: string = ''): string => {
  const params: Record<string, string | number> = {
    filename: file.name,
    type: file.type,
    size: file.size,
    path: [path, file.name].filter(p => p).join('/'),
  }
  return Object
    .keys(params)
    .filter(key => params[key])
    .map(key => {
      return `${key}=${encodeURIComponent(params[key])}`
    })
    .join('&')
}

export const apiUploader = ({
  url,
  file,
  path,
  token,
  onProgress,
}: {
  url: string,
  file: File,
  path: string,
  token?: string,
  onProgress: (values: ProgressEvent) => void,
}): Promise<IField_Storage_File> => {
  return new Promise((resolve, reject) => {

    token = token || localStorage.getItem('token')
    const xhr = new XMLHttpRequest()

    const eventOnProgress = (e: ProgressEvent) => {
      if (e.lengthComputable) {
        onProgress(e)
      }
    }

    const eventOnFailure = (e: ProgressEvent | string) => {
      return reject(e)
    }
    
    const eventOnSuccess = (result: IField_Storage_File) => {
      return resolve(result)
    }

    xhr.upload.addEventListener("progress", eventOnProgress, false)
    xhr.upload.addEventListener("error", eventOnFailure)
    xhr.upload.addEventListener("abort", eventOnFailure)

    xhr.onreadystatechange = () => {
      const { readyState, status } = xhr
      if (readyState === 4) {
        const headers = xhr.getAllResponseHeaders()

        const response = headers.indexOf('content-type: application/json') >= 0 ?
          JSON.parse(xhr.response) : 
          xhr.response

        if (status < 400) {
          eventOnSuccess(response)
        }
        else {
          const bodyMessage = response.error ?
            response.error :
            response
          eventOnFailure(`${status}: ${bodyMessage}`)
        }
      }
    }

    const queryParams = getFileUrlQuery(file, path)
    const seperator = url.indexOf('?') >= 0 ?
      '&' :
      '?'
    let useUrl = url + seperator + queryParams
    xhr.open('POST', useUrl, true)
    xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.setRequestHeader('Content-type', 'application/octet-stream')
    xhr.send(file)
  })
}

/*

  reducers

*/
const reset: ReducerHandler<FileUploadState, void> = (state, action) => {
  resetState(state)
}

const startUploads: ReducerHandler<FileUploadState, PayloadWithFiles> = (state, action) => {
  resetState(state)
  const baseStatus: FileUploadStatus = {}
  state.inProgress = true
  state.status = action.payload.files.reduce((all, file) => {
    all[file.name] = {
      startTime: new Date().getTime(),
      size: file.size,
      uploadedBytes: 0,
      totalBytes: 0,
      percentDone: 0,
      remainingTime: 0,
    }
    return all
  }, baseStatus)
}

const setUploadedProgress: ReducerHandler<FileUploadState, PayloadSetProgress> = (state, action) => {
  const {
    filename,
    values: {
      loaded,
      total,
    },
  } = action.payload

  const percent = Math.floor((loaded / total) * 100)

  const status: any = state.status[filename] || {}
  
  const timeUploading = (new Date().getTime()) - status.startTime
  const timePerPercent = percent > 0 ? timeUploading / percent : 0
  const percentLeft = 100 - percent
  const timeLeft = percentLeft * timePerPercent

  status.percentDone = percent
  status.remainingTime = timeLeft
  status.uploadedBytes = loaded
  status.totalBytes = total

  state.status[filename] = status
}

const finish: ReducerHandler<FileUploadState, void> = (state, action) => {
  state.inProgress = false
}

/*

  thunks

*/
const uploadFiles: ThunkHandler<PayloadUploadFiles, IField_Storage_File[]> = (payload) => async (dispatch, getState) => {
  dispatch(actions.startUploads(payload))
  const info = getProjectInfo(getState)
  const url = getFilestoreUploadUrl(info.project, info.org, payload.location.type)
  try {
    const results = await Bluebird.map(payload.files, async file => apiUploader({
      url,
      file,
      path: payload.location.path,
      onProgress: (values: ProgressEvent) => {
        dispatch(actions.setUploadedProgress({
          filename: file.name,
          values,
        }))
      },
    }))
    dispatch(actions.reset())
    return results
  } catch(e) {
    dispatch(actions.reset())
    throw(e)
  }
}

const uploadBookingFile: ThunkHandler<{
  files: File[],
  path: string,
  token: string,
}, IField_Storage_File[]> = (payload) => async (dispatch, getState) => {
  dispatch(actions.startUploads(payload))
  try {
    const results = await Bluebird.map(payload.files, async file => apiUploader({
      url: '/api/v1/ft_booking_public/file',
      file,
      token: payload.token,
      path: payload.path,
      onProgress: (values: ProgressEvent) => {
        dispatch(actions.setUploadedProgress({
          filename: file.name,
          values,
        }))
      },
    }))
    dispatch(actions.reset())
    return results
  } catch(e) {
    dispatch(actions.reset())
    throw(e)
  }
}

/*

  collections

*/
const reducers = {
  reset,
  startUploads,
  setUploadedProgress,
  finish,
}

export const actions = {
  reset: CreateAction(prefix, `reset`, reset),
  startUploads: CreateAction(prefix, `startUploads`, startUploads),
  setUploadedProgress: CreateAction(prefix, `setUploadedProgress`, setUploadedProgress),
  finish: CreateAction(prefix, `finish`, finish),
  uploadFiles: NetworkThunk(prefix, 'uploadFiles', uploadFiles, {
    snackbar: true,
  }),
  uploadBookingFile: NetworkThunk(prefix, 'uploadBookingFile', uploadBookingFile, {
    snackbar: true,
  }),
}

export const reducer = CreateReducer<FileUploadState>({
  prefix,
  initialState,
  reducers,
})

export default actions